import User from '@models/user/User';
import {
    Commit,
    Dispatch,
} from 'vuex';
import {
    SET_HAS_FETCHED_COMMUNICATION_DATA,
    SET_IS_ACCEPTED_RESPONSIBLE_GAMBLING,
    SET_USER,
} from '@store/modules/data/authentication/mutationTypes';
import { AuthenticationSubscriptionService } from '@store/modules/data/authentication/services/authenticationSubscriptionService';
import {
    AnalyticsLifecycleManager,
    Analytics as AnalyticsSDK,
} from '@superbet-group/web.analytics.sdk';
import {
    deleteIdCardVerificationPending,
    isIdCardVerificationJustBeenApproved,
} from '@models/user/UserDataService';
import { getConfig } from '@superbet-group/web.lib.config';
import { NotificationType } from '@models/user/enums';
import { IAuthenticationState } from '@store/modules/data/authentication/types';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { getDefaultBetslipPurchaseType } from '@app/utils/config/sportConfig';
import { antiFraudService } from '@core/services/antiFraud/AntiFraudService';

let sessionInitialized = false;

export function setSessionState(user: User, { commit, dispatch }:
    { commit: Commit; dispatch: Dispatch; }) {
    // ensures that this action is called at most once per page load

    if (sessionInitialized) {
        throw new Error('Attempted to set session state twice.');
    }
    sessionInitialized = true;

    commit(SET_USER, user);

    AuthenticationSubscriptionService.callPostLoginHandlers(user);

    dispatch('ui/user/preferences/initializeUserPreferences', {}, { root: true });

    handleIdCardVerificationStatus(dispatch, user);

    AnalyticsLifecycleManager
        .getInstance()
        .setUserData({
            userId: user.userId?.toString(),
            userEmail: user.email,
            userUuid: user.userUuid?.toString(),
        });
    AnalyticsSDK
        .getInstance()
        .log('session_user', {
            user_id: user.userId.toString(),
        });
}

export function clearSessionState({ dispatch, commit, state }: { dispatch: Dispatch; commit: Commit, state: IAuthenticationState }) {
    const userId = state.user?.userId;
    commit(SET_USER, null);
    commit(SET_HAS_FETCHED_COMMUNICATION_DATA, false);
    commit(SET_IS_ACCEPTED_RESPONSIBLE_GAMBLING, false);
    clientStorageWithExpiry.remove('user:hasAcceptedResponsibleGambling');
    clientStorageWithExpiry.remove(`user:${userId}:completeRegistrationUserDataKey`);
    clientStorageWithExpiry.remove(`user:${userId}:completeRegistrationiSInvalidUserDataKey`);
    sessionInitialized = false;

    AuthenticationSubscriptionService.callPostLogoutHandlers();

    const defaultBetslipPurchaseType = getDefaultBetslipPurchaseType();
    dispatch('ui/sportOffer/betSlip/setDefaultPurchaseType', defaultBetslipPurchaseType, { root: true });
    dispatch('ui/sportOffer/closeFloatingStream', null, { root: true });
    dispatch('ui/user/notifications/clearNotifications', null, { root: true });
    dispatch('ui/user/account/clearAll', null, { root: true });
    dispatch('data/captiveKyc/clearKycSessionStatus', null, { root: true });
    // If there isn't user data there shouldn't be active bonus.
    // We clear it in order not to do any payout calculations or show it on betSlip.
    dispatch('ui/user/bonuses/clearActiveBonus', null, { root: true });
    dispatch('ui/inbox/clearInbox', null, { root: true });
    dispatch('setSessionStartTime', null);
    dispatch('ui/user/promotions/setArePromotionsFetched', false, { root: true });

    AnalyticsLifecycleManager.getInstance().clearUserData();

    antiFraudService.cleanup();
}

export function handleIdCardVerificationStatus(dispatch: Dispatch, user: User) {
    if (
        !isIdCardVerificationJustBeenApproved(user)
        || getConfig().user.arePushNotificationsEnabled
    ) {
        return;
    }

    deleteIdCardVerificationPending(user);
    dispatch('ui/user/notifications/removeNotification', NotificationType.idCardVerificationPending,
        { root: true });

    dispatch(
        'ui/user/notifications/addNotification',
        {
            title: 'identity document confirm',
            type: NotificationType.idCardVerificationApproved,
        },
        { root: true },
    );
}
